import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Truck, Store, Scissors, UtensilsCrossed, Gift, ChevronRight, Check, Loader2 } from "lucide-react";
import ArteknosLogo from "@/components/ArteknosLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  {
    icon: Truck,
    title: "Livraison Express",
    tagline: "Moto · Voiture · Camion",
    desc: "Colis livrés en moins de 30 min à travers Dakar, suivi GPS temps réel.",
  },
  {
    icon: UtensilsCrossed,
    title: "Food",
    tagline: "Restaurants partenaires",
    desc: "Vos plats favoris livrés chauds en quelques minutes.",
  },
  {
    icon: Store,
    title: "Boutiques",
    tagline: "Commerçants vérifiés",
    desc: "Commandez chez vos marques locales, paiement cash ou mobile money.",
  },
  {
    icon: Scissors,
    title: "ARTISANS",
    tagline: "Plombiers · Couturiers · +12 métiers",
    desc: "Devis instantanés, paiement sécurisé en escrow Wave.",
  },
];

export default function WelcomeBonusOnboarding({ user, onClose, onCreditUpdate }) {
  const [step, setStep] = useState(0); // 0 = welcome, 1 = services, 2 = claim
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [newBalance, setNewBalance] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const claimBonus = async () => {
    if (!user?.id) return;
    setClaiming(true);
    try {
      const { data } = await axios.post(`${API}/auth/claim-welcome-bonus`, { user_id: user.id });
      setClaimed(true);
      setNewBalance(data.credit_balance);
      if (!data.already_claimed) {
        toast.success(data.message);
        onCreditUpdate?.(data.credit_balance);
      } else {
        toast.info("Bonus déjà réclamé");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de réclamer le bonus");
    } finally {
      setClaiming(false);
    }
  };

  const handleFinish = () => {
    onClose?.();
  };

  // --- Step 0: Welcome animé gold ---
  if (step === 0) {
    return (
      <div className="fixed inset-0 z-[9999] bg-[#0F172A]/95 backdrop-blur-md flex items-center justify-center px-4" data-testid="welcome-onboarding-step-0">
        <div className={`relative w-full max-w-lg bg-[#1E293B] border border-[#D4AF37]/30 rounded-[2.5rem] p-8 md:p-10 text-white shadow-2xl shadow-[#D4AF37]/20 transition-all duration-700 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {/* Halos animés */}
          <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full bg-[#D4AF37]/25 blur-3xl animate-pulse" />
          <div className="absolute -bottom-20 -right-20 w-60 h-60 rounded-full bg-[#003594]/30 blur-3xl animate-pulse" />

          <div className="relative text-center">
            {/* Logo ARTEKNOS animé */}
            <div className="mx-auto mb-6 animate-in fade-in zoom-in duration-700 flex justify-center">
              <ArteknosLogo size="2xl" />
            </div>

            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 px-4 py-1.5 rounded-full mb-4 text-[10px] font-bold uppercase tracking-[0.25em]">
              <Sparkles className="w-3.5 h-3.5" />
              Bienvenue dans l'élite
            </div>

            <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tight text-white mb-3 leading-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
              Bonjour <span className="bg-gradient-to-r from-[#D4AF37] to-[#F5D675] bg-clip-text text-transparent">{user?.full_name?.split(' ')[0] || "à vous"}</span> !
            </h1>
            <p className="text-sm text-gray-300 mb-8 max-w-sm mx-auto leading-relaxed">
              Vous venez de rejoindre <strong className="text-[#D4AF37]">ARTEKNOS</strong>, la plateforme premium de livraison et services à Dakar. Un cadeau vous attend à la fin.
            </p>

            <button
              onClick={() => setStep(1)}
              data-testid="welcome-onboarding-next-0"
              className="group w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full py-4 text-sm font-black uppercase tracking-widest shadow-xl shadow-[#D4AF37]/30 transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              Découvrir ARTEKNOS
              <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>

            <button
              onClick={handleFinish}
              data-testid="welcome-onboarding-skip"
              className="mt-4 text-[10px] text-gray-500 hover:text-gray-300 uppercase tracking-widest font-bold"
            >
              Passer l'introduction
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Step 1: Tour des 4 services ---
  if (step === 1) {
    return (
      <div className="fixed inset-0 z-[9999] bg-[#0F172A]/95 backdrop-blur-md flex items-center justify-center px-4 py-6 overflow-y-auto" data-testid="welcome-onboarding-step-1">
        <div className="relative w-full max-w-2xl bg-[#1E293B] border border-white/10 rounded-[2.5rem] p-6 md:p-8 text-white shadow-2xl">
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#D4AF37]/15 blur-3xl" />

          <div className="relative">
            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === 1 ? "w-8 bg-[#D4AF37]" : i < 1 ? "w-1.5 bg-[#D4AF37]/60" : "w-1.5 bg-white/15"
                  }`}
                />
              ))}
            </div>

            <div className="text-center mb-6">
              <p className="text-[10px] text-[#D4AF37] font-bold uppercase tracking-[0.25em] mb-2">Étape 2 / 3</p>
              <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                Votre <span className="text-[#D4AF37]">conciergerie</span> en 4 services
              </h2>
              <p className="text-xs text-gray-400 uppercase tracking-widest">Tout ce que vous pouvez faire depuis l'app</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {SERVICES.map((service, idx) => {
                const Icon = service.icon;
                return (
                  <div
                    key={service.title}
                    className="bg-[#0F172A] border border-white/10 rounded-2xl p-4 hover:border-[#D4AF37]/40 transition-all animate-in fade-in slide-in-from-bottom-2 duration-500"
                    style={{ animationDelay: `${idx * 100}ms` }}
                    data-testid={`welcome-service-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-black text-sm uppercase tracking-tight text-white leading-tight">{service.title}</h3>
                        <p className="text-[10px] text-[#D4AF37]/80 uppercase tracking-widest font-bold mb-1">{service.tagline}</p>
                        <p className="text-xs text-gray-400 leading-relaxed">{service.desc}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(0)}
                data-testid="welcome-onboarding-back-1"
                className="px-5 py-3 rounded-full bg-transparent border border-white/20 text-gray-300 hover:bg-white/5 hover:text-white text-xs font-bold uppercase tracking-widest"
              >
                Retour
              </button>
              <button
                onClick={() => setStep(2)}
                data-testid="welcome-onboarding-next-1"
                className="group flex-1 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full py-3 text-xs font-black uppercase tracking-widest shadow-xl shadow-[#D4AF37]/30 transition-all active:scale-95 flex items-center justify-center gap-2"
              >
                Mon cadeau de bienvenue
                <Gift className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Step 2: Claim 500 FCFA bonus ---
  return (
    <div className="fixed inset-0 z-[9999] bg-[#0F172A]/95 backdrop-blur-md flex items-center justify-center px-4" data-testid="welcome-onboarding-step-2">
      <div className="relative w-full max-w-lg bg-[#1E293B] border border-[#D4AF37]/40 rounded-[2.5rem] p-8 md:p-10 text-white shadow-2xl shadow-[#D4AF37]/30">
        {/* Halos gold */}
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#D4AF37]/30 blur-3xl animate-pulse" />
        <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-[#D4AF37]/20 blur-3xl animate-pulse" />

        <div className="relative text-center">
          {/* Progress dots */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === 2 ? "w-8 bg-[#D4AF37]" : "w-1.5 bg-[#D4AF37]/60"
                }`}
              />
            ))}
          </div>

          <p className="text-[10px] text-[#D4AF37] font-bold uppercase tracking-[0.25em] mb-3">Étape 3 / 3 · Votre cadeau</p>

          {!claimed ? (
            <>
              {/* Boîte cadeau animée */}
              <div className="relative mx-auto mb-6 w-28 h-28">
                <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] rounded-3xl rotate-6 shadow-2xl shadow-[#D4AF37]/40 animate-pulse" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Gift className="w-14 h-14 text-[#0F172A] relative z-10" strokeWidth={2.5} />
                </div>
              </div>

              <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight text-white mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                500 <span className="text-[#D4AF37]">FCFA</span>
              </h2>
              <p className="text-sm text-gray-300 mb-2">de crédit offert pour démarrer</p>
              <p className="text-[11px] text-gray-500 uppercase tracking-widest font-bold mb-8">
                Utilisable immédiatement sur Livraison · Food · Boutiques · Artisans
              </p>

              <button
                onClick={claimBonus}
                disabled={claiming}
                data-testid="welcome-onboarding-claim-btn"
                className="group w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 disabled:opacity-60 text-[#0F172A] rounded-full py-4 text-sm font-black uppercase tracking-widest shadow-xl shadow-[#D4AF37]/40 transition-all active:scale-95 flex items-center justify-center gap-2"
              >
                {claiming ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Crédit en cours…</>
                ) : (
                  <>Réclamer mes 500 FCFA <Sparkles className="w-4 h-4" /></>
                )}
              </button>

              <p className="text-[10px] text-gray-500 mt-3 uppercase tracking-widest font-bold">
                Offre réservée aux nouveaux clients · 1 fois / compte
              </p>
            </>
          ) : (
            <>
              {/* État succès */}
              <div className="relative mx-auto mb-6 w-28 h-28 flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full shadow-2xl shadow-emerald-400/40" />
                <Check className="w-14 h-14 text-white relative z-10" strokeWidth={3} />
              </div>

              <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                Cadeau <span className="text-emerald-400">activé</span>
              </h2>
              {newBalance != null && (
                <p className="text-sm text-gray-300 mb-1">
                  Nouveau solde : <strong className="text-[#D4AF37] text-lg">{newBalance.toLocaleString("fr-FR")} FCFA</strong>
                </p>
              )}
              <p className="text-[11px] text-gray-500 uppercase tracking-widest font-bold mb-8">
                Profitez-en pour votre première commande !
              </p>

              <button
                onClick={handleFinish}
                data-testid="welcome-onboarding-finish-btn"
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full py-4 text-sm font-black uppercase tracking-widest shadow-xl shadow-[#D4AF37]/40 transition-all active:scale-95"
              >
                Commencer à commander
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Hook pour détecter si on doit afficher l'onboarding premium
export function useWelcomeBonusOnboarding(user) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    // Clients uniquement, pas encore réclamé, venant juste de s'inscrire OU pas encore vu ce dialog
    if (user.role !== "client") return;
    if (user.welcome_bonus_claimed) return;

    const justRegistered = sessionStorage.getItem("just_registered");
    const alreadyShown = localStorage.getItem(`welcome_bonus_shown_${user.id}`);
    if (justRegistered || !alreadyShown) {
      setShow(true);
      sessionStorage.removeItem("just_registered");
      localStorage.setItem(`welcome_bonus_shown_${user.id}`, "1");
    }
  }, [user?.id, user?.role, user?.welcome_bonus_claimed]);

  return { show, dismiss: () => setShow(false) };
}
